import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, AlertTriangle, RotateCcw, Search, Layers, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { LeasesTable, LeaseMutateDrawer, DeleteLeaseDialog, TerminateLeaseDialog, CancelLeaseDialog, CompleteLeaseDialog, LeaseDetailPanel } from '@/components/leases'
import { SavedViewTabs, FilterPill, ColumnGroupsDropdown, RecordStatusPill } from '@/components/data-table'
import { useLeases, useDeleteLease, useRestoreLease, useTerminateLease, useCancelLease, useCompleteLease } from '@/hooks/useLeases'
import { useLocations } from '@/hooks/useLocations'
import { useLeaseViewStore } from '@/stores/leaseViewStore'
import { useAddPlaneWizardStore } from '@/stores/addPlaneWizardStore'
import { AddPlaneWizard } from '@/components/ramp-wizard'
import { useAppContext } from '@/hooks/routing'
import {
  LEASE_SAVED_VIEWS,
  LEASE_STATUS_FILTER_OPTIONS,
  CONTRACT_TYPE_FILTER_OPTIONS,
  LEASE_COLUMN_GROUPS,
  LEASE_GROUPING_OPTIONS,
  LEASE_STATUS_LABELS,
  type LeaseViewId,
  type LeaseTableRow,
  type LeaseStatus,
  type LeaseGroupingOption,
  type ContractType,
  getDaysUntilExpiry,
  calculateRevenueAtRisk,
  formatCurrency,
  getContractTypeFromUnit,
} from '@/types/leases'

/** Export lease data to CSV */
function exportLeasesCsv(data: LeaseTableRow[]) {
  const headers = [
    'Contract #', 'Client', 'Unit', 'Building', 'Type',
    'Aircraft', 'Start Date', 'End Date', 'Monthly Rate', 'Status',
    'Security Deposit', 'Renewal Status', 'Auto Renew',
  ]

  const rows = data.map((r) => [
    r.contract_number,
    r.client_name || '',
    r.unit_code || '',
    r.building_name || '',
    r.unit_type || '',
    r.primary_aircraft_tail ? `${r.primary_aircraft_tail}${r.primary_aircraft_type ? ` · ${r.primary_aircraft_type}` : ''}` : '',
    r.start_date || '',
    r.end_date || '',
    r.monthly_rate?.toString() || '',
    LEASE_STATUS_LABELS[r.status] || r.status || '',
    r.security_deposit?.toString() || '',
    r.renewal_status || '',
    r.auto_renew ? 'Yes' : 'No',
  ])

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell: string) => `"${cell}"`).join(','))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `leases_${new Date().toISOString().split('T')[0]}.csv`
  link.click()
}

/**
 * Lease Contracts management page (v2.2)
 *
 * Compact 2-row header:
 * - Row 1: Title + Saved View Tabs + Add Lease button
 * - Row 2: Search + Quick Filter Pills + Column Groups + Grouping + Export
 */
export function LeasesPage() {
  const { locationId, locationName, scope } = useAppContext()
  const navigate = useNavigate()
  const { openWizard } = useAddPlaneWizardStore()

  const {
    activeView,
    setActiveView,
    quickFilters,
    setStatusFilter,
    resetFiltersToViewDefaults,
    getIsModified,
    selectedLeaseId,
    setSelectedLeaseId,
    search,
    setSearch,
    groupBy,
    setGroupBy,
    visibleColumnGroups,
    toggleColumnGroup,
    recordStatus,
    setRecordStatus,
  } = useLeaseViewStore()

  // Quick filter state for contract type and location
  const [contractTypeFilter, setContractTypeFilter] = useState<ContractType[]>([])
  const [locationFilter, setLocationFilter] = useState<string[]>([])

  // Sheet/Dialog state
  const [sheetState, setSheetState] = useState<{
    open: boolean
    leaseId?: string
    renewalLeaseId?: string
  }>({ open: false })
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    contractNumber: string
  } | null>(null)

  // Fetch all leases (pass recordStatus filter)
  const { data: leases, isLoading } = useLeases({ recordStatus })

  // Fetch locations for multi-location filter
  const { data: locations } = useLocations()
  const showLocationFilter = (locations?.length ?? 0) > 1

  // Location filter options
  const locationFilterOptions = useMemo(
    () => (locations ?? []).map((l) => ({ value: l.id, label: l.name })),
    [locations]
  )

  // Delete mutation
  const deleteLease = useDeleteLease()

  // Restore mutation
  const restoreLease = useRestoreLease()

  // Lifecycle mutations + dialog targets
  const terminateMutation = useTerminateLease()
  const cancelMutation = useCancelLease()
  const completeMutation = useCompleteLease()
  const [terminateTarget, setTerminateTarget] = useState<{ id: string; contractNumber: string } | null>(null)
  const [cancelTarget, setCancelTarget] = useState<{ id: string; contractNumber: string } | null>(null)
  const [completeTarget, setCompleteTarget] = useState<{ id: string; contractNumber: string } | null>(null)

  const isModified = getIsModified()

  // Filter leases by active view + quick filters
  const filteredLeases = useMemo(() => {
    if (!leases) return []

    let result = leases

    // Apply status quick filter (empty array = all statuses)
    if (quickFilters.status.length > 0) {
      result = result.filter((l) => quickFilters.status.includes(l.status))
    }

    // Apply contract type filter
    if (contractTypeFilter.length > 0) {
      result = result.filter((l) =>
        contractTypeFilter.includes(getContractTypeFromUnit(l.unit_type))
      )
    }

    // Apply location filter
    if (locationFilter.length > 0) {
      result = result.filter((l) => locationFilter.includes(l.location_id))
    }

    // Expiring view: additionally filter to leases expiring within 90 days
    if (activeView === 'expiring') {
      result = result.filter((l) => {
        const days = getDaysUntilExpiry(l.end_date)
        return days !== null && days >= 0 && days <= 90
      })
    }

    return result
  }, [leases, activeView, quickFilters, contractTypeFilter, locationFilter])

  // View counts (before quick filters, so tabs show total per view)
  const viewCounts = useMemo(() => {
    if (!leases) return { active_portfolio: 0, expiring: 0, drafts: 0, all: 0 } as Record<LeaseViewId, number>

    const activePortfolioCount = leases.filter(
      (l) => l.status === 'active' || l.status === 'expiring'
    ).length

    const expiringCount = leases.filter((l) => {
      const days = getDaysUntilExpiry(l.end_date)
      return days !== null && days >= 0 && days <= 90
    }).length

    const draftCount = leases.filter(
      (l) => l.status === 'draft' || l.status === 'pending_signature'
    ).length

    return {
      active_portfolio: activePortfolioCount,
      expiring: expiringCount,
      drafts: draftCount,
      all: leases.length,
    } as Record<LeaseViewId, number>
  }, [leases])

  // Revenue at risk (for Expiring view banner)
  const revenueAtRisk = useMemo(() => {
    if (!leases) return { monthlyTotal: 0, yearlyTotal: 0, count: 0 }
    return calculateRevenueAtRisk(leases)
  }, [leases])

  // Selected lease for detail panel
  const selectedLease = useMemo(() => {
    if (!selectedLeaseId || !leases) return null
    return leases.find((l) => l.id === selectedLeaseId) ?? null
  }, [selectedLeaseId, leases])

  // Handlers
  const handleCreate = () => {
    openWizard({
      source: 'lease',
      locationId: locationId || undefined,
    })
  }

  const handleEdit = (id: string) => {
    setSelectedLeaseId(null)
    setSheetState({ open: true, leaseId: id })
  }

  const handleDelete = (id: string, contractNumber: string) => {
    setDeleteTarget({ id, contractNumber })
  }

  const handleRestore = (id: string) => {
    restoreLease.mutate(id)
  }

  const handleRowClick = (id: string) => {
    setSelectedLeaseId(id)
  }

  const handleStartRenewal = (leaseId: string) => {
    setSheetState({ open: true, renewalLeaseId: leaseId })
  }

  const handleViewOnMap = (unitId: string) => {
    // Navigate to ramp page — the unit will be highlighted
    const slug = scope === 'location' && locationId
      ? window.location.pathname.split('/')[2] // extract location slug from current path
      : undefined
    if (slug) {
      navigate(`/${slug}/ramp?unit=${unitId}`)
    } else {
      navigate(`/ramp?unit=${unitId}`)
    }
  }

  const handleSheetOpenChange = (open: boolean) => {
    setSheetState((prev) => ({
      open,
      leaseId: open ? prev.leaseId : undefined,
      renewalLeaseId: open ? prev.renewalLeaseId : undefined,
    }))
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      await deleteLease.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    }
  }

  const subtitle =
    scope === 'location' && locationName
      ? `at ${locationName}`
      : undefined

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Row 1: Title + Saved View Tabs + Add Lease */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-baseline gap-2 shrink-0">
          <h1 className="text-2xl font-bold tracking-tight">Lease Contracts</h1>
          {subtitle && (
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {subtitle}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <SavedViewTabs
            views={LEASE_SAVED_VIEWS}
            activeView={activeView}
            onViewChange={setActiveView}
            counts={viewCounts}
            isModified={isModified}
          />
        </div>

        <Button onClick={handleCreate} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          Add Lease
        </Button>
      </div>

      {/* Row 2: Search + Quick Filter Pills + Toolbar */}
      <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contract#, client, tail#..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>

        <div className="h-4 w-px bg-border mx-1" />

        <FilterPill
          label="Contract Type"
          options={CONTRACT_TYPE_FILTER_OPTIONS}
          selected={contractTypeFilter}
          onChange={(values) => setContractTypeFilter(values as ContractType[])}
        />

        <FilterPill
          label="Status"
          options={LEASE_STATUS_FILTER_OPTIONS}
          selected={quickFilters.status}
          onChange={(values) => setStatusFilter(values as LeaseStatus[])}
        />

        {showLocationFilter && (
          <FilterPill
            label="Location"
            options={locationFilterOptions}
            selected={locationFilter}
            onChange={setLocationFilter}
          />
        )}

        <RecordStatusPill value={recordStatus} onChange={setRecordStatus} />
        {isModified && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFiltersToViewDefaults}
            className="h-7 gap-1.5 text-xs text-muted-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </Button>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <ColumnGroupsDropdown
            groups={LEASE_COLUMN_GROUPS}
            visibleGroups={visibleColumnGroups}
            onToggle={toggleColumnGroup}
          />

          <Select value={groupBy} onValueChange={(v) => setGroupBy(v as LeaseGroupingOption)}>
            <SelectTrigger className="h-8 w-[160px]">
              <Layers className="mr-2 h-3.5 w-3.5" />
              <SelectValue placeholder="Group by..." />
            </SelectTrigger>
            <SelectContent>
              {LEASE_GROUPING_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => exportLeasesCsv(filteredLeases)}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Revenue-at-Risk Banner (Expiring view only) */}
      {activeView === 'expiring' && revenueAtRisk.count > 0 && (
        <ExpiringLeasesBanner
          count={revenueAtRisk.count}
          monthlyTotal={revenueAtRisk.monthlyTotal}
          yearlyTotal={revenueAtRisk.yearlyTotal}
        />
      )}

      {/* Leases Table */}
      <div className="flex-1 min-h-0">
        <LeasesTable
          data={filteredLeases}
          isLoading={isLoading}
          activeView={activeView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRowClick={handleRowClick}
          onRestore={handleRestore}
          onTerminate={(id, cn) => setTerminateTarget({ id, contractNumber: cn })}
          onCancel={(id, cn) => setCancelTarget({ id, contractNumber: cn })}
          onComplete={(id, cn) => setCompleteTarget({ id, contractNumber: cn })}
          onStartRenewal={handleStartRenewal}
          onViewOnMap={handleViewOnMap}
        />
      </div>

      {/* Footer Summary Bar */}
      {/* <LeasesSummaryFooter leases={filteredLeases} activeView={activeView} /> */}

      {/* Detail Panel (Sheet or Dialog) */}
      <LeaseDetailPanel
        lease={selectedLease}
        open={!!selectedLeaseId}
        onOpenChange={(open) => {
          if (!open) setSelectedLeaseId(null)
        }}
        onEdit={handleEdit}
      />

      {/* Add Plane Wizard (for creating new leases) */}
      <AddPlaneWizard locationId={locationId || undefined} />

      {/* Edit/Renewal Lease Drawer (edit & renewal only — new leases use wizard) */}
      <LeaseMutateDrawer
        open={sheetState.open}
        onOpenChange={handleSheetOpenChange}
        leaseId={sheetState.leaseId}
        renewalLeaseId={sheetState.renewalLeaseId}
        lockedLocationId={scope === 'location' && locationId ? locationId : undefined}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteLeaseDialog
        open={!!deleteTarget}
        onOpenChange={handleCloseDeleteDialog}
        contractNumber={deleteTarget?.contractNumber ?? ''}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteLease.isPending}
      />

      {/* Terminate Dialog */}
      <TerminateLeaseDialog
        open={!!terminateTarget}
        onOpenChange={(open) => { if (!open) setTerminateTarget(null) }}
        contractNumber={terminateTarget?.contractNumber ?? ''}
        onConfirm={(params) => {
          if (!terminateTarget) return
          terminateMutation.mutate(
            { id: terminateTarget.id, ...params },
            { onSuccess: () => setTerminateTarget(null) }
          )
        }}
        isPending={terminateMutation.isPending}
      />

      {/* Cancel Dialog */}
      <CancelLeaseDialog
        open={!!cancelTarget}
        onOpenChange={(open) => { if (!open) setCancelTarget(null) }}
        contractNumber={cancelTarget?.contractNumber ?? ''}
        onConfirm={(params) => {
          if (!cancelTarget) return
          cancelMutation.mutate(
            { id: cancelTarget.id, cancellation_reason: params.cancellation_reason },
            { onSuccess: () => setCancelTarget(null) }
          )
        }}
        isPending={cancelMutation.isPending}
      />

      {/* Complete Dialog */}
      <CompleteLeaseDialog
        open={!!completeTarget}
        onOpenChange={(open) => { if (!open) setCompleteTarget(null) }}
        contractNumber={completeTarget?.contractNumber ?? ''}
        onConfirm={() => {
          if (!completeTarget) return
          completeMutation.mutate(completeTarget.id, {
            onSuccess: () => setCompleteTarget(null),
          })
        }}
        isPending={completeMutation.isPending}
      />
    </div>
  )
}

// ============================================
// Expiring Leases Banner
// ============================================

interface ExpiringLeasesBannerProps {
  count: number
  monthlyTotal: number
  yearlyTotal: number
}

function ExpiringLeasesBanner({ count, monthlyTotal, yearlyTotal }: ExpiringLeasesBannerProps) {
  return (
    <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/30 p-4 flex-shrink-0">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/50">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-yellow-800 dark:text-yellow-200">
            Expiring Within 90 Days
          </h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            {count} {count === 1 ? 'lease' : 'leases'} | {formatCurrency(monthlyTotal)}/mo at risk
          </p>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-yellow-800 dark:text-yellow-200">
            {formatCurrency(yearlyTotal)}
          </div>
          <div className="text-xs text-yellow-600 dark:text-yellow-400">annual revenue at risk</div>
        </div>
      </div>
    </div>
  )
}
